import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ContentModal,
  type ContentModalProps,
} from "@/shared/ui/core/ContentModal";
import type { ModalHeaderTone } from "@/shared/ui/core/Modal";
import type { ControlSize } from "@/shared/ui/core/types";

export type EntityPeekModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Optional icon in front of title (kích thước = icon X: size-4). */
  icon?: ReactNode;
  /** Presentation style: 'badge' or 'inline'. Default 'badge'. */
  iconVariant?: ContentModalProps['iconVariant'];
  /** Color theme for icon badge. Default 'info'. */
  iconTone?: ContentModalProps['iconTone'];
  /** Header bar color. Default 'info' (xanh dương cho modal thông tin). */
  headerTone?: ModalHeaderTone;
  /** Custom classes for title icon. */
  iconClassName?: string;
  /** Short entity key / doc id — not training copy. */
  description?: string;
  children: ReactNode;
  /**
   * When set, footer primary **Mở trang đầy đủ** navigates here and closes peek.
   * Omit for fragment peeks (e.g. SO line) with no route.
   */
  fullPageTo?: string;
  /** Open `fullPageTo` in a new browser tab instead of in-app navigate. */
  fullPageInNewTab?: boolean;
  size?: ContentModalProps["size"];
  height?: ContentModalProps["height"];
  /** Footer button size. Default stays the shared modal size. */
  footerSize?: ControlSize;
  className?: string;
  bodyClassName?: string;
};

/**
 * Read-only related-entity peek (D-DT10).
 * Presentational shell — caller owns fetch / domain body.
 * Not for create/edit (`EntityFormDialog`) or confirm (`ConfirmationDialog`).
 * MX4: full-page navigation carries `state.returnTo` = host path for Ops ←.
 */
export function EntityPeekModal({
  open,
  onOpenChange,
  title,
  icon,
  iconVariant = "badge",
  iconTone = "info",
  headerTone = "info",
  iconClassName,
  description,
  children,
  fullPageTo,
  fullPageInNewTab = false,
  size = "2xl",
  height = "tall",
  footerSize,
  className,
  bodyClassName,
}: EntityPeekModalProps) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <ContentModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      icon={icon}
      iconVariant={iconVariant}
      iconTone={iconTone}
      headerTone={headerTone}
      iconClassName={iconClassName}
      description={description}
      size={size}
      height={height}
      actionSize={footerSize}
      lockOutsideClose={false}
      className={className}
      bodyClassName={bodyClassName}
      primaryAction={
        fullPageTo
          ? {
              id: "open-to",
              label: "Mở trang đầy đủ",
              onClick: () => {
                if (fullPageInNewTab) {
                  window.open(fullPageTo, "_blank", "noopener,noreferrer");
                  return;
                }
                const returnTo = `${location.pathname}${location.search}`;
                onOpenChange(false);
                navigate(fullPageTo, { state: { returnTo } });
              },
            }
          : undefined
      }
    >
      {children}
    </ContentModal>
  );
}
